import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import { styled } from '@mui/material/styles';
import MuiBox from '@mui/material/Box';
import Hint from '../Hint/Hint.js';
import { getBorderMock } from '../../utils/getBorderMock.js';
import 'date-fns';
import useGlobalStyles from '../../theme/global.js';

const _TextArea = styled('textarea', {
    shouldForwardProp: prop => !['inactive', 'modifiedManually', 'error', 'typography', 'variantOverrides', 'withBorder'].includes(prop),
}) `
  width: 100%;
  max-width: 100%;
  border: ${({ theme, withBorder }) => (withBorder ? `1px solid ${theme.palette.N40.main}` : 'none')};
  border-radius: 4px;
  outline: none;
  line-height: 16px;
  min-width: 48px;
  min-height: 28px;
  cursor: pointer;
  overflow: overlay;
  transition: color ${({ theme }) => theme.transitions.duration.fast} linear,
    background-color ${({ theme }) => theme.transitions.duration.fast} linear,
    border ${({ theme }) => theme.transitions.duration.fast} linear,
    fill ${({ theme }) => theme.transitions.duration.fast} linear;

  ::placeholder {
    font-style: italic;
    opacity: 1;
  }
  ::-webkit-resizer {
    background-image: url('data:image/svg+xml,<svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.85355 0.853553C7.04882 0.658291 7.04882 0.341709 6.85355 0.146447C6.65829 -0.0488155 6.34171 -0.0488155 6.14645 0.146447L0.146447 6.14645C-0.0488155 6.34171 -0.0488155 6.65829 0.146447 6.85355C0.341709 7.04882 0.658291 7.04882 0.853553 6.85355L6.85355 0.853553ZM6.85355 4.85355C7.04882 4.65829 7.04882 4.34171 6.85355 4.14645C6.65829 3.95118 6.34171 3.95118 6.14645 4.14645L4.14645 6.14645C3.95118 6.34171 3.95118 6.65829 4.14645 6.85355C4.34171 7.04882 4.65829 7.04882 4.85355 6.85355L6.85355 4.85355Z" fill="%234F6B84"/></svg>');
    background-size: 7px 7px;
    background-repeat: no-repeat;
    background-position: calc(100% - 2px) calc(100% - 2px);
  }
  ::-webkit-scrollbar-corner {
    background-color: transparent;
  }
  ${({ theme, modifiedManually, typography, variantOverrides }) => `
    background-color: ${theme.palette.N70.main};
    color: ${theme.palette[modifiedManually ? 'warning' : 'N10'].main};
    caret-color: ${theme.palette.white.main};
    padding: ${theme.spacing(0.5, 3.5, 0.5, 1.5)};
    font-family: ${(typography === null || typography === void 0 ? void 0 : typography.fontFamily) || theme.typography.body1.fontFamily};
    font-size: ${(typography === null || typography === void 0 ? void 0 : typography.fontSize) || theme.typography.body1.fontSize};
    font-weight: ${(typography === null || typography === void 0 ? void 0 : typography.fontWeight) || theme.typography.body1.fontWeight};
    font-style: ${(typography === null || typography === void 0 ? void 0 : typography.fontStyle) || theme.typography.body1.fontStyle};
    line-height: ${(typography === null || typography === void 0 ? void 0 : typography.lineHeight) || theme.typography.body1.lineHeight};

    ${variantOverrides};

    ::placeholder {
      color: ${theme.palette.N30.main};
    }
    :focus {
      cursor: text;
      color: ${theme.palette.N10.main};
      ::placeholder {
        color: ${theme.palette.N30.main};
      }
    }
    :hover:focus::placeholder {
      color: ${theme.palette.primary.dark};
    }
    :hover {
      background-color: ${theme.palette.N60.main};
    }
  `}
  ${({ theme, disabled, error }) => !disabled &&
    !error &&
    `:hover {
        color: ${theme.palette.primary.main};
        ::placeholder {
          color: ${theme.palette.primary.dark};
        }
      }
  `}
    ${({ theme, inactive }) => inactive &&
    `
    background-color: transparent;
    color: ${theme.palette.N20.main};
  `}
  ${({ theme, error, disabled }) => !disabled &&
    error &&
    `
    :focus {
      color: ${theme.palette.N10.main};
    }
    :hover {
      color: ${theme.palette.primary.main};
    }
    :not(:focus) {
      color: ${theme.palette.error.main};
    }
    ::placeholder {
      color: ${theme.palette.error.main};
    }
  `}
  ${({ theme, disabled, inactive }) => disabled &&
    `
    background-color: ${inactive ? 'transparent' : theme.palette.N80.main};
    opacity: 0.4;
    resize: none;
    overflow-y: hidden;
    color: ${theme.palette.N30.main};
    ::placeholder {
      color: ${theme.palette.N30.main};
    }
    :hover {
      cursor: not-allowed;
      background-color: ${inactive ? 'transparent' : theme.palette.N80.main};
    }
  `};
`;
const TextAreaWrapper = styled(MuiBox, {
    shouldForwardProp: prop => !['inactive', 'disabled', 'error', 'value'].includes(prop),
}) `
  display: flex;
  position: relative;
  ${({ theme }) => getBorderMock(theme)}
  ${({ theme }) => `
    :focus-within:after {
      border-color: ${theme.palette.primary.main};
    }
  `}
  ${({ theme, error, disabled }) => !disabled &&
    error &&
    `
    :after {
      border-color: ${theme.palette.error.main};
    };
  `};
`;
const TextArea = (_a) => {
    var { inactive, disabled, error, value, onChange, hint, hintDuration, 'data-testid': testId } = _a, rest = __rest(_a, ["inactive", "disabled", "error", "value", "onChange", "hint", "hintDuration", 'data-testid']);
    useGlobalStyles();
    return (jsx(Hint, Object.assign({ title: hint, duration: hintDuration }, { children: jsx(TextAreaWrapper, Object.assign({ error: error, inactive: inactive, disabled: disabled, value: value, className: "styled-scrollbar" }, { children: jsx(_TextArea, Object.assign({ disabled: disabled, error: error, inactive: inactive, value: value, onChange: onChange, "data-testid": testId }, rest)) })) })));
};

export { TextArea as default };
//# sourceMappingURL=TextArea.js.map
